import { useScrollRestoration } from 'gatsby';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { StoryblokRichtext } from 'storyblok-rich-text-react-renderer';
import { LayoutContext } from '../../contexts/LayoutContext';
import { PageContext } from '../../contexts/PageContext';
import { Blok } from '../../interfaces/Blok.interface';
import { CTA } from '../../interfaces/CTA.interface';
import { StoryblokArticle } from '../../interfaces/StoryblokArticle.interface';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import { StoryblokNode } from '../../interfaces/StoryblokNode.interface';
import { formatStringForUrl } from '../../utils/formatStringForUrl/formatStringForUrl.util';
import BreadcrumbBar from '../BreadcrumbBar/BreadcrumbBar';
import { CTABarSmallBlokProps } from '../CTABarSmall/CTABarSmall';
import Header from '../Header/Header';
import TestingGrid from '../TestingGrid/TestingGrid.molecule';
import Col from '../Utils/Col/Col';
import Container from '../Utils/Container/Container';
import Grid from '../Utils/Grid/Grid';
import CategoryLandingPageHero from './CategoryLandingPageHero';
import CategoryLandingPageSection from './CategoryLandingPageSection';
import CategoryLandingPageSideBar from './CategoryLandingPageSideBar';

export interface CategoryLandingPageSectionBlokProps extends Blok {
  heading: string;
  body: StoryblokRichtext;
  cta: CTA[];
  top_row_articles: StoryblokNode<StoryblokArticle>[];
  cta_bar: CTABarSmallBlokProps[];
  bottom_row_articles: StoryblokNode<StoryblokArticle>[];
}

interface CategoryLandingPageBlokProps extends Blok {
  hero_background_image: StoryblokAsset;
  hero_overline?: string;
  hero_heading: string;
  hero_body?: string;
  hero_cta: Blok[];
  category_list_title: string;
  category_landing_page_sections: CategoryLandingPageSectionBlokProps[];
  body?: Blok[];
  transparent_header: boolean;
  show_contact_bar: boolean;
}

interface CategoryLandingPageProps {
  blok: CategoryLandingPageBlokProps;
}

const getHeadingInQueryParamFormat = (header: string): string => {
  return header
    .toLowerCase()
    .replaceAll(/[^a-zA-Z ]/g, '')
    .replaceAll(' ', '-');
};

const CategoryLandingPage: React.FC<CategoryLandingPageProps> = ({ blok }) => {
  const { header, footer, contact_bar, breadcrumbs } =
    useContext(LayoutContext);
  const {
    hero_background_image,
    hero_overline = undefined,
    hero_heading,
    hero_body = undefined,
    hero_cta = [],
    category_list_title,
    category_landing_page_sections = [],
    body = [],
    show_contact_bar,
  } = blok;

  const isHeroRequired = useMemo(() => {
    if (hero_background_image?.filename && hero_heading) return true;
    return false;
  }, [hero_background_image, hero_heading]);

  const isCategoryLandingPageBodyRequired = useMemo(() => {
    /*-- this needs more i think. maybe drilling down into the rows and checking that we have a value? --*/
    if (category_landing_page_sections) return true;
    return false;
  }, [category_landing_page_sections]);

  const articlesRef = useRef<HTMLDivElement>(null);

  const scrollRestoration = useScrollRestoration(`category-landing-page`);
  const [
    activeCategoryLandingPageSection,
    setActiveCategoryLandingPageSection,
  ] = useState<string>(
    formatStringForUrl(category_landing_page_sections[0]?.heading || '')
  );

  const [componentHeight, setComponentHeight] = useState<number>(0);

  useEffect(() => {
    if (articlesRef.current) {
      const height = articlesRef.current.getBoundingClientRect().height;
      setComponentHeight(height);
    }

    const handleResize = () => {
      if (articlesRef.current) {
        const height = articlesRef.current.getBoundingClientRect().height;
        setComponentHeight(height);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <PageContext.Provider
      value={{
        headerIsTransparent: false,
        isHeaderSearchFunctionalityRequired: true,
        showContactBar: blok.show_contact_bar,
      }}
    >
      <div className="flex flex-col h-screen justify-between">
        {header && (
          <Header
            blok={{ ...header.content, isFixed: false }}
            key={header._uid}
          />
        )}
        <main
          className="mb-auto"
          {...storyblokEditable(blok)}
          {...scrollRestoration}
        >
          {isHeroRequired && (
            <CategoryLandingPageHero
              hero_background_image={hero_background_image}
              hero_overline={hero_overline}
              hero_heading={hero_heading}
              hero_body={hero_body}
              hero_cta={hero_cta}
            />
          )}
          {breadcrumbs && <BreadcrumbBar breadcrumbs={breadcrumbs} />}
          {isCategoryLandingPageBodyRequired && (
            <Container>
              <div className="py-24 h-full" ref={articlesRef}>
                <Grid>
                  <Col span={{ xl: 3 }}>
                    <CategoryLandingPageSideBar
                      categoryListTitle={category_list_title}
                      categoryLandingPageSections={
                        category_landing_page_sections
                      }
                      activeCategoryLandingPageSection={
                        activeCategoryLandingPageSection
                      }
                      setActiveCategoryLandingPageSection={
                        setActiveCategoryLandingPageSection
                      }
                      categoryLandingHeight={componentHeight}
                    />
                  </Col>
                  <div className="col-span-12 xl:col-span-9 space-y-12">
                    {category_landing_page_sections.map(
                      (categoryLandingPageSection) => (
                        <CategoryLandingPageSection
                          categoryLandingPageSections={
                            category_landing_page_sections
                          }
                          categoryLandingPageSection={
                            categoryLandingPageSection
                          }
                          setActiveCategoryLandingPageSection={
                            setActiveCategoryLandingPageSection
                          }
                          key={categoryLandingPageSection._uid}
                        />
                      )
                    )}
                  </div>
                </Grid>
              </div>
            </Container>
          )}
          {body?.map((nestedBlok: Blok) => (
            <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
          ))}
          {show_contact_bar && contact_bar && (
            <StoryblokComponent
              blok={contact_bar.content}
              key={contact_bar._uid}
            />
          )}
        </main>
        {footer && (
          <StoryblokComponent blok={footer.content} key={footer._uid} />
        )}
        <TestingGrid />
      </div>
    </PageContext.Provider>
  );
};

export default CategoryLandingPage;
